import React, { useEffect, useState } from 'react';
import { TextField, MenuItem, Grid, IconButton, Typography, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Trigger } from './types';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions, LeadFormActions } from '../../../Services/_actions/index.js';
import { useParams } from 'react-router';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../Shared/BotNumberDropdown/index.tsx';
import TemplateDropdown from '../../Shared/TemplateDropdown/TemplateDropdown.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import RoleDropdown from '../../Shared/RoleDropdown/index.tsx';
import { useRecoilState, useRecoilValue } from 'recoil';
import { assignedCampaignUserAtom } from '../../../Services/_states/index.js';
import ProfileDropdown from '../../Shared/ProfileDropdown/index.tsx';

type Props = {
  trigger: Trigger;
  onChange: (updated: Trigger) => void;
  onRemove: () => void;
  sNo: any;
};

const triggerOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Sms', value: 'sms' },
  { label: 'Whatsapp', value: 'whatsapp' },
  { label: 'Bot', value: 'bot' },
  { label: 'Move to campaign', value: 'moveToCampaign' },
  { label: 'Assign Lead', value: 'assignLead' },
];

const TriggerForm: React.FC<Props> = ({ trigger, onChange, onRemove, sNo }) => {
  const { showSnackBar } = useSnackBar();
  const {
    fetchCampaignMappings,
    getFormsByCampaignId,
    fetchStatusAndSubStatus,
    postLeadScheduler,
  } = LeadFormActions();
  const userInfo = useRecoilValue(userInformationAtom);
  const { campaignId, leadFormId, statusId } = useParams();
  const currentStatusId = statusId;
  const [childMappings, setChildMappings] = useState<any[]>([]);
  const [childCampaignFormList, setChildCampaignFormList] = useState<any[]>([]);
  const [mergedWebFormFields, setMergedWebFormFields] = useState<any[]>([]);
  const [timeUnit, setTimeUnit] = useState<string>('seconds');
  const [nextStatusList, setNextStatusList] = useState<any[]>([]);
  const userList = useRecoilValue(assignedCampaignUserAtom);
  const allUsers = (userList?.[campaignId as any] ?? []) as any[];
  const ensureOptionShape = (u: any) => {
    const found = allUsers?.find((x: any) => String(x?.id) === String(u?.id));
    return found ?? { id: u?.id, username: u?.username ?? u?.name };
  };

  const userHasProfile = (u: any, profileId: string | number) => {
    if (!u || profileId == null) return false;

    const pid = String(profileId).trim();
    const uid = String(u.profile_id ?? u.profile?.id ?? '').trim();

    return uid === pid;
  };

  // const userHasRole = (u: any, roleId: string | number) => {
  //   if (!u) return false;
  //   if (u.role?.id) return String(u.role.id) === String(roleId);
  //   if (u.role_id) return String(u.role_id) === String(roleId);
  //   if (Array.isArray(u.roles))
  //     return u.roles.some((r: any) => String(r?.id ?? r) === String(roleId));
  //   if (Array.isArray(u.groups))
  //     return u.groups.some((r: any) => String(r?.id ?? r) === String(roleId));
  //   return false;
  // };

  const [profileUserMap, setProfileUserMap] = useState<
    Record<string, Array<{ id: any; username: string }>>
  >((trigger as any)?.profileUserMap ?? {});

  const [selectedProfiles, setSelectedProfiles] = useState<Array<{ id: any; name?: string }>>(
    Array.isArray((trigger as any)?.selectedProfiles) ? (trigger as any)?.selectedProfiles : []
  );

  // const [roleUserMap, setRoleUserMap] = useState<
  //   Record<string, Array<{ id: any; username: string }>>
  // >((trigger as any)?.roleUserMap ?? {});

  // const [selectedRoles, setSelectedRoles] = useState<Array<{ id: any; name?: string }>>(
  //   Array.isArray((trigger as any)?.selectedRoles) ? (trigger as any)?.selectedRoles : []
  // );

  const initialStakeholderTarget =
    (trigger as any)?.stakeholderTarget ??
    ((trigger as any)?.stakeholders === 'end_customer' ||
    (trigger as any)?.stakeholders === 'endcustomer'
      ? 'endcustomer'
      : 'profile');

  const [stakeholderTarget, setStakeholderTarget] = useState<'profile' | 'endcustomer'>(
    initialStakeholderTarget
  );

  useEffect(() => {
    const { stakeholderTarget: _st, roleUserMap: _rum, ...rest } = trigger;

    if (stakeholderTarget === 'endcustomer') {
      onChange({
        ...rest,
        stakeholders: 'end_customer',
        users: [],
        selectedRoles: [],
        roleUserMap: {},
      });
      return;
    }

    // const stakeholdersPayload = (selectedRoles ?? []).map((r) => {
    //   const rid = String(r.id);
    //   const usersForRole = (roleUserMap[rid] ?? []).map((u: any) => ({
    const stakeholdersPayload = (selectedProfiles ?? []).map((p) => {
      const pid = String(p.id);
      const usersForProfile = (profileUserMap[pid] ?? []).map((u: any) => ({
        id: u?.id,
        username: u?.username,
      }));
      return { id: Number(p.id), name: p?.name, users: usersForProfile };
      // return { id: Number(r.id), name: r?.name, users: usersForRole };
    });

    // const flatUsers = Object.values(roleUserMap)
    const flatUsers = Object.values(profileUserMap)
      .flat()
      .map((u: any) => ({ id: u?.id, username: u?.username }));

    onChange({
      ...rest,
      users: flatUsers,
      stakeholders: stakeholdersPayload,
      selectedProfiles,
      profileUserMap,
      // selectedRoles,
      // roleUserMap,
    });
  }, [profileUserMap, selectedProfiles, stakeholderTarget]);

  // }, [roleUserMap, selectedRoles, stakeholderTarget]);

  useEffect(() => {
    if (stakeholderTarget !== 'profile') return;

    const selectedProfileIds = new Set((selectedProfiles ?? []).map((p) => String(p?.id)));
    const nextMap: typeof profileUserMap = {};
    Object.entries(profileUserMap).forEach(([pid, users]) => {
      if (selectedProfileIds.has(String(pid))) nextMap[pid] = users;
    });
    if (JSON.stringify(nextMap) !== JSON.stringify(profileUserMap)) {
      setProfileUserMap(nextMap);
    }
  }, [selectedProfiles, stakeholderTarget]);

  useEffect(() => {
    const savedMap = (trigger as any)?.profileUserMap;
    const savedProfiles = (trigger as any)?.selectedProfiles;
    const st = (trigger as any)?.stakeholders;

    const profileNameById: Record<string, string | undefined> = Array.isArray(st)
      ? st.reduce((acc: any, r: any) => {
          if (r?.id != null) acc[String(r.id)] = r?.name;
          return acc;
        }, {})
      : {};

    if (savedMap && Object.keys(savedMap).length) {
      const normalized: Record<string, any[]> = {};
      Object.entries(savedMap).forEach(([pid, arr]) => {
        normalized[pid] = (arr as any[])?.map(ensureOptionShape);
      });
      setProfileUserMap(normalized);

      if (Array.isArray(savedProfiles) && savedProfiles.length) {
        const profilesWithNames = savedProfiles.map((p: any) => ({
          id: p.id,
          name: p?.name ?? profileNameById[String(p.id)],
        }));
        setSelectedProfiles(profilesWithNames);
        setStakeholderTarget('profile');
      }
    }

    if (Array.isArray(st) && st.every((x) => x?.id !== undefined)) {
      const profiles = st.map((p: any) => ({ id: p.id, name: p.name }));
      const byProfile: Record<string, any[]> = {};
      st.forEach((p: any) => {
        const pid = String(p.id);
        byProfile[pid] = (p.users ?? []).map(ensureOptionShape);
      });
      if (profiles.length) setSelectedProfiles(profiles);
      if (Object.keys(byProfile).length) setProfileUserMap(byProfile);
      if (profiles.length) setStakeholderTarget('profile');
    }
  }, [allUsers]);

  const handleChange = (key: keyof Trigger, value: any) => {
    let updatedTrigger = { ...trigger, [key]: value };

    // if (key === 'trigger_type' && value === 'moveToStatus') {
    //   delete updatedTrigger.childCampaign;
    //   delete updatedTrigger.childCampaignForm;
    //   delete updatedTrigger.webFormFields;
    //   delete updatedTrigger.stakeholder_id;
    // }

    // if (trigger.trigger_type === 'moveToStatus') {
    //   delete updatedTrigger.childCampaign;
    // }

    onChange(updatedTrigger);
  };

  const fetchStatusList = async () => {
    try {
      const res = await fetchStatusAndSubStatus({ leadFormId: leadFormId });
      if (res?.status === 200) {
        setNextStatusList(res.data || []);
        // console.log(res, "jkshdfjkk");
        setNextStatusList(res.data || []);
      }
    } catch (error) {
      showSnackBar('Failed to load status list.', 'error');
    }
  };

  const handleSelectChildCampaign = async (val: any) => {
    // setChildCampaignFormList([]);
    // onChange({ ...trigger, childCampaignForm: null });
    try {
      handleChange('childCampaign', val || '');
      // await fetchForms(val?.child_campaign_id);
    } catch {
      showSnackBar('Failed to select child campaign. Please try again.', 'error');
    }
  };

  const getCampaignMapping = async () => {
    try {
      const res = await fetchCampaignMappings(campaignId);
      if (res?.status === 200) {
        setChildMappings(res.data.child_mappings || []);
      } else {
        showSnackBar('Unable to fetch campaign mappings. Please try again later.', 'error');
      }
    } catch {
      showSnackBar('An unexpected error occurred while fetching campaign mappings.', 'error');
    }
  };

  useEffect(() => {
    getCampaignMapping();
  }, []);

  const handleSelectOrganisation = (org: any) => {
    let selected: { name: string; organisation_id: string } | null = null;
    if (org?.organisation_id) {
      selected = { name: org?.name, organisation_id: org?.organisation_id ?? '' };
    }
    // setRoleUserMap({});
    // setSelectedRoles([]);
    // setStakeholderTarget('role');
    setProfileUserMap({});
    setSelectedProfiles([]);
    setStakeholderTarget('profile');

    onChange({
      ...trigger,
      organisation: selected,
      template: null,
      template_id: '',
      botNumber: null,
      bot_number: '',
      roleUserMap: {},
      selectedRoles: [],
      users: [],
      stakeholders: [],
    });
  };

  return (
    // <Box key={sNo} sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
    //   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    //     <Typography variant="subtitle1" fontWeight={500}>
    //       Trigger & Activity
    //     </Typography>
    //     <IconButton onClick={onRemove} color="error" size="small">
    //       <DeleteIcon />
    //     </IconButton>
    //   </Box>
    <Grid container spacing={2} alignItems="center" sx={{ pt: 1, pb: 1, mb: 1 }}>
      <Grid item xs={12} sm={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Trigger Type"
          value={trigger.trigger_type}
          onChange={(e) => handleChange('trigger_type', e.target.value)}
        >
          {triggerOptions.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {trigger.trigger_type === 'email' && (
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Sender Email"
            value={trigger.sender_email || ''}
            onChange={(e) => handleChange('sender_email', e.target.value)}
          />
        </Grid>
      )}

      {trigger.trigger_type === 'sms' && (
        <>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              fullWidth
              label="Sender ID"
              value={trigger.sender_id || ''}
              onChange={(e) => handleChange('sender_id', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="DLT ID"
              size="small"
              value={trigger.dlt_id || ''}
              onChange={(e) => handleChange('dlt_id', e.target.value)}
            />
          </Grid>
        </>
      )}
      {/* {['whatsapp', 'bot', 'assignLead'].includes(trigger?.trigger_type) && ( */}
      {['whatsapp', 'bot'].includes(trigger?.trigger_type) && (
        <>
          <Grid item xs={12} sm={3}>
            <OrganisationDropdown
              label="Select Organisation"
              value={trigger?.organisation ?? null}
              onChange={handleSelectOrganisation}
              autoSelectValue={false}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <BotNumberDropdown
              organisation={trigger?.organisation ?? null}
              label="Select Bot Number"
              value={trigger?.botNumber || null}
              onChange={(val) => {
                if (val?.wa_phone_number) {
                  onChange({
                    ...trigger,
                    bot_number: val?.wa_phone_number,
                    botNumber: {
                      wa_phone_number: val?.wa_phone_number,
                      app_name: val?.app_name,
                      template: null,
                      template_id: '',
                    },
                  });
                } else {
                  onChange({
                    ...trigger,
                    bot_number: '',
                    botNumber: null,
                    template: null,
                    template_id: '',
                  });
                }
              }}
            />
          </Grid>
        </>
      )}
      {trigger.trigger_type === 'bot' && (
        <>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Flow ID"
              size="small"
              value={trigger.flow_id || ''}
              onChange={(e) => handleChange('flow_id', e.target.value)}
            />
          </Grid>
        </>
      )}

      {trigger.trigger_type !== 'moveToCampaign' && (
        <>
          {/* {trigger.trigger_type === 'whatsapp' || trigger.trigger_type === 'assignLead' && (
              <Grid item xs={12} sm={3}>
                <TemplateDropdown
                  value={trigger?.template || ''}
                  dropdownFilters={{
                    organisationId: trigger?.organisation?.organisation_id || '',
                    botNumber: trigger?.bot_number || '',
                  }}
                  onChange={(val) => {
                    if (val?.wa_Id) {
                      onChange({
                        ...trigger,
                        template_id: val?.wa_Id,
                        template: { wa_Id: val?.wa_Id, name: val?.name },
                      });
                    } else {
                      onChange({ ...trigger, template_id: '', template: null });
                    }
                  }}
                  label="Choose a Template"
                  disabled={!trigger?.organisation?.organisation_id || !trigger?.bot_number} // 🔑 disable until both are selected
                />
              </Grid>
            )} */}
          {/* {(trigger.trigger_type === 'whatsapp' || trigger.trigger_type === 'assignLead') && ( */}
          {trigger.trigger_type === 'whatsapp' && (
            <Grid item xs={12} sm={3}>
              <TemplateDropdown
                value={trigger?.template || ''}
                dropdownFilters={{
                  organisationId: trigger?.organisation?.organisation_id || '',
                  botNumber: trigger?.bot_number || '',
                }}
                onChange={(val) => {
                  if (val?.wa_Id) {
                    onChange({
                      ...trigger,
                      template_id: val?.wa_Id,
                      template: { wa_Id: val?.wa_Id, name: val?.name },
                    });
                  } else {
                    onChange({ ...trigger, template_id: '', template: null });
                  }
                }}
                label="Choose a Template"
                disabled={!trigger?.organisation?.organisation_id || !trigger?.bot_number}
              />
            </Grid>
          )}
          {trigger.trigger_type === 'whatsapp' && (
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Stakeholder Type"
                value={stakeholderTarget}
                onChange={(e) => {
                  const val = e.target.value as 'profile' | 'endcustomer';
                  setStakeholderTarget(val);
                  if (val === 'endcustomer') {
                    onChange({
                      ...trigger,
                      stakeholderTarget: 'endcustomer',
                      stakeholders: 'endcustomer',
                      users: [],
                      selectedRoles: [],
                      roleUserMap: {},
                    });
                  }
                }}
              >
                <MenuItem value="endcustomer">End Customer</MenuItem>
                <MenuItem value="profile">Profile</MenuItem>
                {/* <MenuItem value="role">Role</MenuItem> */}
              </TextField>
            </Grid>
          )}

          {trigger.trigger_type === 'assignLead' ||
          (trigger.trigger_type === 'whatsapp' && stakeholderTarget === 'profile') ? (
            <>
              {/* 1) Select multiple roles */}
              <Grid item xs={12} sm={4}>
                {/* <RoleDropdown
                    value={selectedRoles}
                    onChange={(val) => {
                      const roles = val?.map((r: any) => ({ id: r?.id, name: r?.name })) ?? [];
                      setSelectedRoles(roles);
                    }}
                    organisationId={trigger?.organisation?.organisation_id ?? ''}
                    multiSelect
                    label="Select Roles"
                  /> */}
                <ProfileDropdown
                  value={selectedProfiles}
                  onChange={(val) => {
                    const profiles = val?.map((p: any) => ({ id: p?.id, name: p?.name })) ?? [];
                    setSelectedProfiles(profiles);
                  }}
                  organisationId={
                    trigger?.organisation?.organisation_id || userInfo?.organisation_id || ''
                  }
                  multiSelect
                  label="Select Profiles"
                />
              </Grid>

              {/* 2) For each selected role, render a filtered user dropdown */}
              {/* {(selectedRoles ?? []).map((role: any) => {
                  const rid = String(role?.id);
                  const roleUsers = allUsers.filter((u) => userHasRole(u, rid)); */}
              {(selectedProfiles ?? []).map((profile: any) => {
                const pid = String(profile?.id);
                const profileUsers = allUsers.filter((u) => userHasProfile(u, pid));
                console.log('🔍 allUsers', allUsers);
                console.log('📋 selected profile id', pid);

                const globallySelectedIds = new Set(
                  Object.values(profileUserMap)
                    .flat()
                    .map((u) => String(u.id))
                );

                return (
                  <Grid key={pid} item xs={12} sm={4}>
                    <SearchableDropdown
                      options={profileUsers}
                      value={profileUserMap[pid] ?? []}
                      labelKey="username"
                      valueKey="id"
                      label={`Select Users — ${profile?.name ?? 'Profile'}`}
                      multiSelect
                      getOptionDisabled={(option) =>
                        globallySelectedIds.has(String(option?.id)) &&
                        !(profileUserMap[pid] ?? []).some(
                          (u) => String(u.id) === String(option?.id)
                        )
                      }
                      onChange={(val) => {
                        const next = {
                          ...profileUserMap,
                          [pid]:
                            (val ?? []).map((u: any) => ({
                              id: u?.id,
                              username: u?.username,
                            })) || [],
                        };
                        setProfileUserMap(next);
                      }}
                    />
                  </Grid>
                );
              })}
            </>
          ) : trigger.trigger_type === 'whatsapp' && stakeholderTarget === 'endcustomer' ? (
            <></>
          ) : (
            <Grid item xs={12} sm={3}>
              <RoleDropdown
                value={trigger?.stakeholders ?? []}
                onChange={(val) => {
                  const selectedUsers =
                    val?.map((user: any) => ({ id: user?.id, name: user?.name })) ?? [];
                  onChange({ ...trigger, stakeholders: selectedUsers });
                }}
                organisationId={trigger?.organisation?.organisation_id ?? ''}
                multiSelect
                label="Select Stakeholders"
              />
            </Grid>
          )}

          {/* {trigger.trigger_type === 'assignLead' ? (
              <Grid item xs={12} sm={3}>
                <SearchableDropdown
                  options={userList[campaignId] ?? []}
                  value={trigger?.users ?? []}
                  labelKey="username"
                  valueKey="id"
                  onChange={(val) => {
                    if (val?.length > 0) {
                      onChange({
                        ...trigger,
                        users: val?.map((user: any) => ({ id: user?.id, username: user?.username })),
                      });
                    } else {
                      onChange({ ...trigger, users: [] });
                    }
                  }}
                  label="Add Users"
                  multiSelect={true}
                  getOptionDisabled={(option) =>
                    (trigger?.users ?? []).some(u => u?.id === option?.id)
                  }
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={3}>
                <RoleDropdown
                  value={trigger?.stakeholders ?? []}
                  onChange={(val) => {
                    const selectedUsers =
                      val?.map((user: any) => ({ id: user?.id, name: user?.name })) ?? [];
                    onChange({ ...trigger, stakeholders: selectedUsers });
                  }}
                  organisationId={trigger?.organisation?.organisation_id ?? ''}
                  multiSelect={true}
                  label="Select Stakeholders"
                />
              </Grid>
            )} */}
        </>
      )}

      {trigger.trigger_type === 'moveToCampaign' && (
        <>
          <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={childMappings}
              labelKey="child_campaign_name"
              valueKey="child_campaign_id"
              label="Select Child Campaign"
              value={trigger.childCampaign}
              onChange={(e) => handleSelectChildCampaign(e)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
        <IconButton onClick={onRemove} color="error" size="small">
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
    // </Box>
  );
};

export default TriggerForm;
